import { DecimalPipe, NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { TranslocoModule } from '@jsverse/transloco';
import {
  presetOf,
  TRAJECTORY_BOUNDS,
  TRAJECTORY_PRESETS,
  TrajectoryPreset,
  TrajectorySettings,
  TrajectoryToggle,
} from './person-motion';
import { SiteBuildingState } from './site-building.state';

type NumericKey = keyof typeof TRAJECTORY_BOUNDS;

/** Boutons segmentés : mêmes classes que les contrôles du bâtiment. */
const ON = 'bg-gray-900 text-white dark:bg-slate-100 dark:text-slate-900';
const OFF =
  'border border-gray-200 text-gray-700 hover:bg-gray-100 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-700';

/**
 * Réglages de démo du personnage 3D en direct (ADR-017) : trois préréglages, et le détail sous
 * « Avancé » (écrêtage, lissage, murs, rejet des points impossibles, rattrapage). Le titre compte les
 * positions ignorées. Repliés par défaut ; mémorisés dans le navigateur par
 * {@link SiteBuildingState.setTrajectory}.
 */
@Component({
  selector: 'lib-trajectory-settings',
  standalone: true,
  imports: [TranslocoModule, DecimalPipe, NgTemplateOutlet],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @let s = building().trajectory();
    <details class="mt-2 rounded border border-gray-200 text-xs dark:border-slate-600">
      <summary class="cursor-pointer select-none px-2.5 py-1.5 font-semibold text-gray-600 dark:text-slate-300">
        {{ 'siteViewMap.building.trajectory.title' | transloco }}
        @if (building().rejected(); as n) {
          <span class="font-normal text-gray-500 dark:text-slate-400">
            · {{ 'siteViewMap.building.trajectory.rejected' | transloco: { n } }}
          </span>
        }
      </summary>
      <div class="flex flex-col gap-2.5 px-2.5 pb-2.5 pt-1">
        <div class="flex flex-wrap items-center gap-1.5" role="group">
          @for (p of presets; track p) {
            <button
              type="button"
              class="h-7 rounded px-2.5 text-xs font-bold"
              [class]="preset() === p ? on : off"
              [attr.aria-pressed]="preset() === p"
              (click)="building().setTrajectory(presetValues[p])">
              {{ 'siteViewMap.building.trajectory.preset.' + p | transloco }}
            </button>
          }
        </div>
        <details>
          <summary class="cursor-pointer select-none font-semibold text-gray-600 dark:text-slate-300">
            {{ 'siteViewMap.building.trajectory.advanced' | transloco }}
          </summary>
          <div class="flex flex-col gap-2.5 pt-2">
            <label class="flex items-center gap-2 font-semibold text-gray-700 dark:text-slate-200">
              <input type="checkbox" [checked]="s.gate" (change)="toggle('gate', $event)" />
              {{ 'siteViewMap.building.trajectory.gate' | transloco }}
            </label>
            <ng-container
              *ngTemplateOutlet="slider; context: { key: 'maxSpeedMs', unit: 'm/s', digits: '1.1-1', off: !s.gate }" />
            <label class="flex items-center gap-2 font-semibold text-gray-700 dark:text-slate-200">
              <input type="checkbox" [checked]="s.smoothing" (change)="toggle('smoothing', $event)" />
              {{ 'siteViewMap.building.trajectory.smoothing' | transloco }}
            </label>
            <ng-container
              *ngTemplateOutlet="
                slider;
                context: { key: 'minCutoffHz', unit: 'Hz', digits: '1.2-2', off: !s.smoothing }
              " />
            <ng-container
              *ngTemplateOutlet="slider; context: { key: 'beta', unit: '', digits: '1.2-2', off: !s.smoothing }" />
            <label class="flex items-center gap-2 font-semibold text-gray-700 dark:text-slate-200">
              <input type="checkbox" [checked]="s.walls" (change)="toggle('walls', $event)" />
              {{ 'siteViewMap.building.trajectory.walls' | transloco }}
            </label>
            <ng-container
              *ngTemplateOutlet="
                slider;
                context: { key: 'wallClearanceM', unit: 'm', digits: '1.1-1', off: !s.walls }
              " />
            <ng-container
              *ngTemplateOutlet="
                slider;
                context: { key: 'detourSpeedMs', unit: 'm/s', digits: '1.1-1', off: !s.walls }
              " />
            <label class="flex items-center gap-2 text-gray-700 dark:text-slate-200" [class.opacity-40]="!s.walls">
              <input
                type="checkbox"
                [checked]="s.crossWalls"
                [disabled]="!s.walls"
                (change)="toggle('crossWalls', $event)" />
              {{ 'siteViewMap.building.trajectory.crossWalls' | transloco }}
            </label>
            <label
              class="flex items-center gap-2 font-semibold text-gray-700 dark:text-slate-200"
              [class.opacity-40]="!s.walls">
              <input
                type="checkbox"
                [checked]="s.walkGate"
                [disabled]="!s.walls"
                (change)="toggle('walkGate', $event)" />
              {{ 'siteViewMap.building.trajectory.walkGate' | transloco }}
            </label>
            <ng-container
              *ngTemplateOutlet="
                slider;
                context: { key: 'walkToleranceM', unit: 'm', digits: '1.1-1', off: !s.walls || !s.walkGate }
              " />
            <ng-container
              *ngTemplateOutlet="
                slider;
                context: { key: 'cedeAfter', unit: '', digits: '1.0-0', off: !s.walls || !s.walkGate }
              " />
            <ng-container
              *ngTemplateOutlet="slider; context: { key: 'catchUpS', unit: 's', digits: '1.1-1', off: false }" />
            <ng-container
              *ngTemplateOutlet="slider; context: { key: 'snapM', unit: 'm', digits: '1.0-0', off: false }" />
          </div>
        </details>
      </div>
    </details>

    <ng-template #slider let-key="key" let-unit="unit" let-digits="digits" let-off="off">
      <label class="flex flex-col gap-1" [class.opacity-40]="off">
        <span class="flex justify-between text-gray-600 dark:text-slate-300">
          <span>{{ 'siteViewMap.building.trajectory.' + key | transloco }}</span>
          <span class="font-semibold tabular-nums">{{ value(key) | number: digits }} {{ unit }}</span>
        </span>
        <input
          type="range"
          class="accent-pink-600"
          [min]="bound(key).min"
          [max]="bound(key).max"
          [step]="bound(key).step"
          [value]="value(key)"
          [disabled]="off"
          (input)="set(key, $event)" />
      </label>
    </ng-template>
  `,
})
export class TrajectorySettingsComponent {
  readonly building = input.required<SiteBuildingState>();
  protected readonly presets: TrajectoryPreset[] = ['raw', 'balanced', 'smooth'];
  protected readonly presetValues = TRAJECTORY_PRESETS;
  protected readonly preset = computed(() => presetOf(this.building().trajectory()));
  protected readonly on = ON;
  protected readonly off = OFF;

  protected bound(key: NumericKey): { min: number; max: number; step: number } {
    return TRAJECTORY_BOUNDS[key];
  }

  protected value(key: NumericKey): number {
    return this.building().trajectory()[key];
  }

  protected set(key: NumericKey, event: Event): void {
    const v = Number((event.target as HTMLInputElement).value);
    this.update({ [key]: v });
  }

  protected toggle(key: TrajectoryToggle, event: Event): void {
    this.update({ [key]: (event.target as HTMLInputElement).checked });
  }

  private update(patch: Partial<TrajectorySettings>): void {
    this.building().setTrajectory({ ...this.building().trajectory(), ...patch });
  }
}
